import { useState } from 'react';

import config from 'config';
const { INDEXER_API } = config;

import FungibleTokens from 'services/fungible-tokens';
import { getNames, getRewards, getProjectStatus } from './utils-for-hooks';

export default function useLazyQuery<T>(
  jsonQuery?: (accountId: string, from: number, limit: number) => void
) {
  const [data, setData] = useState<T[]>([]);
  const [error, setError] = useState<string>();
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);

  const fetchCollateral = async (accountId, page = 1) => {
    const limit = 10;
    const from = (page - 1) * limit;

    setIsLoading(from === 0);
    setIsLoadingMore(from > 0);

    try {
      const projects = await FungibleTokens.getUserCollateralProjects({ accountId, from, limit });

      const names = await getNames(projects);
      const rewards = await getRewards(projects);

      if (projects.length > 0 && names.length > 0 && rewards.length > 0) {
        const tableProjects = projects
          .map((project, index) => {
            const findNameById = names.find(
              ({ projectId }) => projectId.toString() === project.projectId.toString()
            );

            const periods = findNameById?.periods;
            const state = rewards[index]?.state ? rewards[index].state : '---';
            const minimumStaked = rewards[index]?.minimum_staked;

            return {
              name: findNameById?.name,
              periods,
              minimumStaked,
              statusState: getProjectStatus({
                periods,
                state,
                minimumStaked,
                status: project.status
              }),
              state: state.toLowerCase() === 'open' ? 'Open' : 'Closed',
              myOpnRewards: rewards[index]?.myRewards
                ? rewards[index].myRewards
                : '---',
              ...project
            };
          })
          .filter((project) => project.name !== undefined);
        setData((prev) => [...prev, ...tableProjects]);
      }
      setHasMore(limit === projects.length);
    } catch (e) {
      setHasMore(false);
      setError(e.message);
    }

    setIsLoading(false);
    setIsLoadingMore(false);
  };

  const handleResponse = async (response) => {
    if (response.ok) {
      setError('');
      const result = await response.json();

      if (result?.hits?.hits && result.hits.hits.length > 0) {
        setData((prev) => [
          ...prev,
          ...result.hits.hits.map((hit) => {
            const fields = {};
            for (const key in hit.fields) {
              fields[key] =
                hit.fields[key][0] !== '' ? hit.fields[key][0] : '---';
            }
            return {
              ...hit._source,
              ...fields
            };
          })
        ]);
      } else {
        setHasMore(false);
      }
    }

    if (!response.ok) {
      const result = await response.text();
      setError(result);
    }
  };

  const sendQuery = async (accountId, page = 1) => {
    const limit = 10;
    const from = (page - 1) * limit;

    setIsLoading(from === 0);
    setIsLoadingMore(from > 0);

    const response = await fetch(`${INDEXER_API}/projects`, {
      method: 'POST',
      body: JSON.stringify(jsonQuery(accountId, from, limit)),
      headers: { 'Content-type': 'application/json; charset=utf-8' }
    });
    await handleResponse(response);

    setIsLoading(false);
    setIsLoadingMore(false);
  };

  return { data, isLoading, isLoadingMore, hasMore, error, page, setPage, refetch: sendQuery, fetchCollateral };
}
